import React, { HTMLAttributes, useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
// import { useRouter } from "next/router";
import { Hooks, Util } from "@xt/xtbase";
import store from "store";
import { post_order } from "api/v4/order";
import Storage from "utils/storage";
import { Dropdown, MenuProps, Tooltip, Checkbox } from "antd";
import XtSvg from "components/xt/svg";
import ModalAlert from "components/antd/modal/alert";
import AppInputNumber from "components/app/input/number";
import useCoinMemo from "components/pages/trade/_hook/useCoinMemo";
import useBalancesAvailable from "components/pages/trade/_hook/useBalancesAvailable";
import Option from "../option";
import Slider from "../slider";
import LoginOrRegister from "../loginOrRegister";

import styles from "./index.module.scss";

import { ClsUpDownEnum } from "store/app";
import { TradeSideEnum } from "store/trade";

const { useTranslation } = Hooks;
const { getUrl, Big } = Util;

interface Props extends HTMLAttributes<HTMLDivElement> {
  tradeSide: TradeSideEnum;
  onSuccess?: () => void;
}

enum MarketTradeType { //市价交易类型
  total = "total", //成交额
  amount = "amount", //数量
}

const Main: React.FC<Props> = ({ className, tradeSide, onSuccess }) => {
  // const router = useRouter();
  const t = useTranslation();

  const { name, type, currentConfig } = store.market;
  // const { currencyQuantity, currencyPrice } = store.balances;
  // const { currencyObj } = store.currency;
  const { tradeRecentOnce, orderConfirm_market } = store.trade;
  const { isLogin } = store.user;

  const { coinQuantityUpperCase, coinPriceUpperCase, coinQuantityPrecisionMarket, coinPricePrecisionMarket } = useCoinMemo();
  const { balancesAvailable, balancesAvailableLabel } = useBalancesAvailable(tradeSide);

  const isBuy = useMemo(() => tradeSide === TradeSideEnum.buy, [tradeSide]);
  const currency = useMemo(() => {
    const ary = name.split("_");
    return tradeSide === TradeSideEnum.buy ? ary[1] : ary[0];
  }, [tradeSide, name]); //当前交易币种

  // const cls = useMemo(() => {
  //   return tradeSide === TradeSideEnum.buy ? ClsUpDownEnum.up : ClsUpDownEnum.down;
  // }, [tradeSide]); //样式

  const [loading, setLoading] = useState(false);

  const [marketTradeType, setMarketTradeType] = useState<MarketTradeType>(tradeSide === TradeSideEnum.buy ? MarketTradeType.total : MarketTradeType.amount);
  const [inputValue, setInputValue] = useState("");
  const [isErrInputValue, setIsErrInputValue] = useState(false);

  const dropdownItems: MenuProps["items"] = useMemo(() => {
    return [
      {
        key: MarketTradeType.total,
        label: (
          <div className={styles.dropTotal} onClick={() => setMarketTradeType(MarketTradeType.total)}>
            <span>{t("trade.totalVol")}</span>
            <Tooltip placement="top" title={isBuy ? t("trade.marketTotalTipBuy") : t("trade.marketTotalTipSell")}>
              <span>
                <XtSvg icon={"faq"} />
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: MarketTradeType.amount,
        label: <a onClick={() => setMarketTradeType(MarketTradeType.amount)}>{t("trade.amount")}</a>,
      },
    ];
  }, [isBuy]);

  const point = useMemo(() => {
    return marketTradeType === MarketTradeType.total ? coinPricePrecisionMarket : coinQuantityPrecisionMarket;
  }, [marketTradeType, coinPricePrecisionMarket, coinQuantityPrecisionMarket]);
  const maxInputValue = useMemo<string>(() => {
    if (isBuy) {
      if (marketTradeType === MarketTradeType.total) {
        return balancesAvailable || "";
      } else {
        if (!tradeRecentOnce) return "";
        return Big(balancesAvailable || 0)
          .div(tradeRecentOnce.p)
          .toFixed(point);
      }
    } else {
      if (marketTradeType === MarketTradeType.total) {
        if (!tradeRecentOnce) return "";
        return Big(balancesAvailable || 0)
          .times(tradeRecentOnce.p)
          .toFixed(point);
      } else {
        return balancesAvailable || "";
      }
    }
    //后面根据最新成交价变更
    // return balancesAvailable || "";
  }, [balancesAvailable, tradeRecentOnce, isBuy, marketTradeType, point]);
  const inputValueConvert = useMemo(() => {
    if (isBuy) {
      if (marketTradeType === MarketTradeType.total) {
        return inputValue;
      } else {
        if (!tradeRecentOnce) return "";
        return Big(inputValue || 0)
          .times(tradeRecentOnce.p)
          .toFixedMax(coinPricePrecisionMarket);
      }
    } else {
      if (marketTradeType === MarketTradeType.total) {
        if (!tradeRecentOnce) return "";
        return Big(inputValue || 0)
          .div(tradeRecentOnce.p)
          .toFixedMax(coinQuantityPrecisionMarket);
      } else {
        return inputValue;
      }
    }
  }, [inputValue, tradeRecentOnce, isBuy, maxInputValue, coinPricePrecisionMarket, coinQuantityPrecisionMarket]);
  const isErrInputValueMemo = useMemo(() => {
    return isErrInputValue || +inputValue - +maxInputValue > 0;
  }, [isErrInputValue, inputValue, maxInputValue]);

  const handleInputValue = useCallback((val) => {
    setInputValue(val);
  }, []);
  const handleSliderChange = useCallback(
    (val, slider) => {
      let value = val;
      if (!+val && !slider) {
        value = "";
      }
      handleInputValue(value);
    },
    [handleInputValue]
  );

  const btnSubmitObj = useMemo(() => {
    const cls = tradeSide === TradeSideEnum.buy ? styles.btnSubmit_buy : styles.btnSubmit_sell;
    let lab = t("trade.login2Register");
    if (isLogin) {
      lab = t(tradeSide === TradeSideEnum.buy ? "trade.buy" : "trade.sell") + " " + coinQuantityUpperCase;
    }
    return {
      cls,
      lab,
    };
  }, [isLogin, tradeSide, coinQuantityUpperCase]);
  const btnSubmitDisabled = useMemo(() => {
    if (!isLogin) return false;
    if (loading || !currentConfig.tradingEnabled) return true;
    return false;
  }, [isLogin, loading, currentConfig]);
  const apiResPostOrder = useCallback(() => {
    if (loading) return;
    setLoading(true);
    const data = {
      symbol: name,
      side: tradeSide,
      type: "MARKET",
      timeInForce: "IOC",
      bizType: type,
      [tradeSide === TradeSideEnum.buy ? "quoteQty" : "quantity"]: inputValueConvert,
    };

    post_order({
      data,
      errorPop: true,
      successPop: true,
    })
      .then((data) => {
        console.log("success", data);
        onSuccess && onSuccess();
      })
      .catch((data) => {
        console.log("error", data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [loading, name, tradeSide, type, inputValueConvert]);
  const handleSubmit = useCallback(() => {
    if (btnSubmitDisabled) return;
    if (!isLogin) {
      const query = "?backurl=" + encodeURIComponent(location.href);
      location.href = getUrl("/accounts/login" + query);
      return;
    }

    if (!+inputValue) return setIsErrInputValue(true);

    if (!orderConfirm_market) return todo();

    let checked = !orderConfirm_market;

    ModalAlert({
      title: t("trade.orderConfirm"),
      okText: t("confirm"),
      width: 450,
      closable: true,
      onOk: (close) => {
        close();
        store.trade.updateState({ orderConfirm_market: !checked });
        Storage.set("orderConfirm_market", !checked);
        todo();
      },
      closeIcon: <XtSvg icon={"close"} />,
      content: (
        <div className={styles.orderConfirm}>
          <div>
            <span>{store.market.formatName(store.market.name)}</span>&nbsp;
            <span className={isBuy ? ClsUpDownEnum.up : ClsUpDownEnum.down}>{isBuy ? t("trade.buy") : t("trade.sell")}</span>
          </div>

          <div>
            <div>
              <div>{t("trade.type")}</div>
              <div>{t("trade.marketOrder")}</div>
            </div>
            <div>
              <div>{t("trade.orderPrice")}</div>
              <div>{t("trade.market")}</div>
            </div>
            {marketTradeType === MarketTradeType.total && (
              <div>
                <div>{t("trade.totalVol")}</div>
                <div>{inputValue + " " + coinPriceUpperCase}</div>
              </div>
            )}
            {marketTradeType === MarketTradeType.amount && (
              <div>
                <div>{t("trade.amount")}</div>
                <div>{inputValue + " " + coinQuantityUpperCase}</div>
              </div>
            )}
          </div>

          <div>
            <Checkbox className={styles.orderConfirmCheckbox} defaultChecked={checked} onChange={(e) => (checked = e.target.checked)}>
              {t("trade.noAlertAndSetTip")}
            </Checkbox>
          </div>
        </div>
      ),
    });

    function todo() {
      if (+inputValue - +maxInputValue > 0) return ModalAlert(t("trade.amountBigTip"));

      apiResPostOrder();
    }
  }, [
    isLogin,
    btnSubmitDisabled,
    inputValue,
    maxInputValue,
    apiResPostOrder,
    orderConfirm_market,
    isBuy,
    marketTradeType,
    coinPriceUpperCase,
    coinQuantityUpperCase,
  ]);

  useEffect(() => {
    setInputValue("");
  }, [tradeRecentOnce, type, tradeSide, marketTradeType]);
  useEffect(() => {
    setMarketTradeType(tradeSide === TradeSideEnum.buy ? MarketTradeType.total : MarketTradeType.amount);
  }, [tradeSide]);
  useEffect(() => {
    setIsErrInputValue(false);
  }, [inputValue]);

  return (
    <div className={cx(styles.main, className)}>
      <div className={styles.nav}>
        <div>
          <span>{t("trade.avbl")}:</span>
          {/*<span className={cls}>{balancesAvailableLabel}</span>*/}
          <span>{balancesAvailableLabel}</span>
          <span>{currency.toUpperCase()}</span>
        </div>
        <Option tradeSide={tradeSide} currency={currency} />
      </div>

      <div className={styles.content}>
        <AppInputNumber
          className={styles.input}
          prefix={t("trade.price")}
          suffix={coinPriceUpperCase}
          disabled={true}
          disabledLabel={t("trade.market")}
          // noBtns={true}
        />
        <AppInputNumber
          className={styles.input}
          prefix={
            tradeRecentOnce ? (
              <Dropdown
                placement={"bottomLeft"}
                menu={{
                  items: dropdownItems,
                  selectable: true,
                  selectedKeys: marketTradeType ? [marketTradeType] : [],
                }}
              >
                <button className={cx("btnTxt btnDrop")} onClick={(e) => e.preventDefault()}>
                  {marketTradeType === MarketTradeType.total ? t("trade.totalVol") : t("trade.amount")}
                </button>
              </Dropdown>
            ) : (
              <span>{marketTradeType === MarketTradeType.total ? t("trade.totalVol") : t("trade.amount")}</span>
            )
          }
          suffix={marketTradeType === MarketTradeType.total ? coinPriceUpperCase : coinQuantityUpperCase}
          value={inputValue}
          onInput={handleInputValue}
          disabled={loading}
          isErr={isErrInputValueMemo}
          point={point}
          isStepPoint={true}
        />

        <Slider className={styles.slider} value={inputValue} max={maxInputValue} point={point} disabled={loading} onChange={handleSliderChange} />

        {isLogin ? (
          <button className={cx("btnTxt", styles.btnSubmit, btnSubmitObj.cls)} disabled={btnSubmitDisabled} onClick={handleSubmit}>
            {btnSubmitObj.lab}
          </button>
        ) : (
          <LoginOrRegister isBuy={isBuy} />
        )}
      </div>
    </div>
  );
};

export default observer(Main);
